import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Copy, Check } from 'lucide-react';

/**
 * COMPONENT: ErrorBoundary (Enhanced)
 *
 * Provides comprehensive error handling:
 * - Catches all React errors
 * - Shows user-friendly error UI
 * - Provides recovery options
 * - Logs errors for debugging
 * - Auto-recovery attempt after timeout
 * - Copy error details to clipboard
 *
 * Fixes: Unhandled errors crashing the app, poor error UX, no error logging
 */

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  autoRecover?: boolean;
  autoRecoverDelay?: number;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
  lastErrorTime: number | null;
  isRecovering: boolean;
  copied: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  private recoveryTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
      lastErrorTime: null,
      isRecovering: false,
      copied: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const now = Date.now();

    console.error('[Error Boundary] Error caught:', error);
    console.error('[Error Boundary] Component stack:', errorInfo.componentStack);

    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1,
      lastErrorTime: now,
    }));

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    if (this.props.autoRecover && this.state.errorCount < 3) {
      const delay = this.props.autoRecoverDelay || 5000;
      console.log(`[Error Boundary] Scheduling auto-recovery in ${delay}ms`);

      this.recoveryTimeoutId = setTimeout(() => {
        console.log('[Error Boundary] Attempting auto-recovery');
        this.handleReset();
      }, delay);
    }
  }

  componentWillUnmount() {
    if (this.recoveryTimeoutId) {
      clearTimeout(this.recoveryTimeoutId);
    }
  }

  handleReset = (): void => {
    console.log('[Error Boundary] Resetting error boundary');

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      isRecovering: true,
    });

    setTimeout(() => {
      this.setState({ isRecovering: false });
    }, 500);

    if (this.recoveryTimeoutId) {
      clearTimeout(this.recoveryTimeoutId);
      this.recoveryTimeoutId = null;
    }
  };

  handleCopyError = async (): Promise<void> => {
    if (!this.state.error) return;

    const errorDetails = [
      `Error: ${this.state.error.toString()}`,
      '',
      'Stack Trace:',
      this.state.error.stack || 'No stack trace available',
      '',
      'Component Stack:',
      this.state.errorInfo?.componentStack || 'No component stack available',
      '',
      `Time: ${new Date().toISOString()}`,
      `Error Count: ${this.state.errorCount}`,
    ].join('\n');

    try {
      await navigator.clipboard.writeText(errorDetails);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
      console.log('[Error Boundary] Error details copied to clipboard');
    } catch (err) {
      console.error('[Error Boundary] Failed to copy to clipboard:', err);
    }
  };

  handleReload = (): void => {
    console.log('[Error Boundary] Reloading page');
    window.location.reload();
  };

  handleGoHome = (): void => {
    console.log('[Error Boundary] Navigating to home');
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-gray-800/50 backdrop-blur-sm border border-gray-700 rounded-lg p-6 sm:p-8">
            <div className="flex items-start space-x-3 sm:space-x-4 mb-6">
              <div className="p-2 sm:p-3 bg-red-400/10 rounded-xl flex-shrink-0">
                <AlertTriangle className="h-6 w-6 sm:h-8 sm:w-8 text-red-400" />
              </div>
              <div className="flex-1">
                <h1 className="text-xl sm:text-2xl font-bold text-white">Something Went Wrong</h1>
                <p className="text-sm sm:text-base text-gray-400 mt-1">
                  An error occurred in the application
                </p>
                {this.state.errorCount > 1 && (
                  <p className="text-xs text-yellow-400 mt-2">
                    This error has occurred {this.state.errorCount} times
                  </p>
                )}
              </div>
            </div>

            {this.state.error && (
              <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-red-400">Error Details:</p>
                  <button
                    onClick={this.handleCopyError}
                    className="flex items-center space-x-1 text-xs text-gray-400 hover:text-white transition-colors"
                  >
                    {this.state.copied ? (
                      <>
                        <Check className="h-3 w-3" />
                        <span>Copied!</span>
                      </>
                    ) : (
                      <>
                        <Copy className="h-3 w-3" />
                        <span>Copy</span>
                      </>
                    )}
                  </button>
                </div>
                <pre className="text-xs text-gray-300 overflow-x-auto max-h-40 overflow-y-auto">
                  {this.state.error.toString()}
                  {this.state.error.stack && (
                    <>
                      {'\n\n'}
                      {this.state.error.stack.split('\n').slice(0, 5).join('\n')}
                    </>
                  )}
                </pre>
              </div>
            )}

            <div className="bg-blue-900/20 border border-blue-800 rounded-lg p-4 mb-6">
              <p className="text-sm font-medium text-blue-400 mb-2">Suggested Actions:</p>
              <ul className="text-xs text-blue-300 space-y-1 list-disc list-inside">
                <li>Try clicking "Try Again" to recover the application</li>
                <li>Check your internet connection</li>
                <li>Clear your browser cache and reload</li>
                <li>If the problem persists, contact support with the error details</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={this.handleReset}
                disabled={this.state.isRecovering}
                className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium text-sm sm:text-base"
              >
                <RefreshCw className={`h-4 w-4 sm:h-5 sm:w-5 ${this.state.isRecovering ? 'animate-spin' : ''}`} />
                <span>{this.state.isRecovering ? 'Recovering...' : 'Try Again'}</span>
              </button>
              <button
                onClick={this.handleReload}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium text-sm sm:text-base"
              >
                Reload Page
              </button>
              <button
                onClick={this.handleGoHome}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium text-sm sm:text-base"
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}